import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useShopDomains from "@babackapp/private-api-client/client/use-shop-domains";
import FormInputWithSelectSuffix from "@babackapp/react-hook-forms-components/form-input-with-select-suffix";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Form, FormField } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import useUpdateNotificationsEmailFrom from "@babackapp/private-api-client/client/use-update-notifications-email-from";
import { z } from "zod";

const DomainListFormSchema = z.object({
  email: z.object({
    input: z.string().min(1),
    select: z.string().min(1),
  }),
});

type DomainListFormValues = z.infer<typeof DomainListFormSchema>;

type DomainListFormProps = {
  defaultValues: DomainListFormValues;
};

export function DomainListForm({ defaultValues }: DomainListFormProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { data: domains } = useShopDomains(shop.id);
  const { mutateAsync: updateEmailFrom } =
    useUpdateNotificationsEmailFrom(shop);

  const form = useForm<DomainListFormValues>({
    resolver: zodResolver(DomainListFormSchema),
    defaultValues,
  });

  const onSubmit = async (values: DomainListFormValues) => {
    await updateEmailFrom(`${values.email.input}@${values.email.select}`);
    form.reset(values);
  };

  const options = useMemo(() => {
    if (!domains?.length) return [];
    return domains
      .filter((domain) => domain.status === "active")
      .map((domain) => domain.name);
  }, [domains]);

  if (!options?.length) return null;

  const emailErrorMessage = form.formState.errors.email?.input?.message;
  const selectErrorMessage = form.formState.errors.email?.select?.message;
  const error = emailErrorMessage || selectErrorMessage || undefined;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2 items-start">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormInputWithSelectSuffix
                label="Email"
                separator="@"
                description="Email address used to send emails to your customers."
                options={options}
                error={error}
                {...field}
              />
            )}
          />
          {form.formState.isDirty && (
            <Button variant="secondary" type="submit">
              {t("misc.save")}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
